"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Trophy } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { formatPrice } from "../helpers/formatting";
import { LEAGUE_NAMES } from "@/lib/league-names";
import type { Teams, Game } from "../types";

// Sidebar that surfaces other live/upcoming games in the same league.
// Pulls /api/sports-live once on mount and filters out the current
// match + any rows where the price split has effectively settled
// (>=99/<=1) since those are "ended in all but name" and listing them
// makes the sidebar feel stale. Hides when no related games remain.
export function MoreGames({
  currentSlug,
  league,
  isEsports,
}: {
  currentSlug: string;
  league: string;
  isEsports?: boolean;
}) {
  const t = useT();
  const [games, setGames] = useState<Array<{ slug: string; teams: Teams; game: Game }>>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/sports-live")
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data.success || !data.state) return;
        const { games: allGames, teams: allTeams } = data.state;
        const related: Array<{ slug: string; teams: Teams; game: Game }> = [];
        for (const [s, g] of Object.entries(allGames) as [string, Game][]) {
          if (s === currentSlug || g.league !== league) continue;
          // Hide finished matches — their prices resolve to 100¢/0¢ which
          // looks broken in the sidebar and the sidebar's purpose is to
          // surface other LIVE/UPCOMING matches the user can still trade.
          if (g.ended) continue;
          const teamsForGame = allTeams[s];
          if (!teamsForGame) continue;
          const hp = teamsForGame.home?.price;
          const ap = teamsForGame.away?.price;
          // Defensive: hide rows where prices are missing OR resolve to a
          // ~100/0 split (means the match has effectively settled even
          // though `ended` hasn't propagated yet from the score feed).
          if (hp == null || ap == null) continue;
          if ((hp >= 0.99 && ap <= 0.01) || (ap >= 0.99 && hp <= 0.01)) continue;
          related.push({ slug: s, teams: teamsForGame, game: g });
        }
        related.sort((a, b) => (a.game.live && !b.game.live ? -1 : !a.game.live && b.game.live ? 1 : 0));
        setGames(related.slice(0, 6));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentSlug, league]);

  if (!games.length) return null;

  // Pretty league name: prefer the canonical map ("fifwc" → "FIFA World Cup")
  // so codes that don't title-case cleanly (fifwc → "Fifwc") read correctly;
  // fall back to dash→space title-case for unmapped codes.
  const prettyLeague =
    LEAGUE_NAMES[league] || league.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="bg-white dark:bg-[#141418] rounded-[5px] border border-gray-100 dark:border-[#1e1e24] overflow-hidden">
      <div className="px-6 py-3 flex items-center gap-2">
        <Trophy className="w-[18px] h-[18px] text-gray-400 dark:text-gray-500" aria-hidden="true" />
        <h3 className="font-monument text-lg text-gray-900 dark:text-white">{t("moreGames.title", { league: prettyLeague })}</h3>
      </div>
      <div className="px-3 pb-3 border-t border-gray-100 dark:border-[#1e1e24] pt-3 space-y-2">
        {games.map(g => {
          const hp = g.teams.home?.price;
          const ap = g.teams.away?.price;
          // Probability split — the bar fills proportionally to home/away
          // implied probability so the user can pick out the favorite at a
          // glance. Falls back to 50/50 if either price is missing.
          const sum = (hp ?? 0) + (ap ?? 0);
          const homePct = sum > 0 ? Math.round(((hp ?? 0) / sum) * 100) : 50;
          const awayPct = 100 - homePct;
          // Time string for upcoming matches — short relative format ("In
          // 2h", "Tomorrow 12:30", "Apr 30 09:00 UTC"). Live matches show
          // "Live" + period instead.
          const startTimeText = (() => {
            if (g.game.live || g.game.ended) return null;
            const ts = g.game.startTime || g.game.timestamp;
            if (!ts) return null;
            const d = new Date(ts);
            if (isNaN(d.getTime())) return null;
            const diffMs = d.getTime() - Date.now();
            const diffH = diffMs / 3.6e6;
            const hh = d.getUTCHours().toString().padStart(2, '0');
            const mm = d.getUTCMinutes().toString().padStart(2, '0');
            if (diffH < 0) return null;
            if (diffH < 1) return t("time.inMinutes", { n: Math.max(1, Math.round(diffMs / 6e4)) });
            if (diffH < 12) return t("time.inHours", { n: Math.round(diffH) });
            const isToday = d.toDateString() === new Date().toDateString();
            const isTomorrow = d.toDateString() === new Date(Date.now() + 86400000).toDateString();
            if (isToday) return t("time.today", { time: `${hh}:${mm}` });
            if (isTomorrow) return t("time.tomorrow", { time: `${hh}:${mm}` });
            // Detect ES locale via a stable translation probe so months
            // render in the user's language ("9 may" vs "May 9").
            const isSpanish = t("time.today", { time: "" }).startsWith("Hoy");
            const month = d.toLocaleString(isSpanish ? 'es-ES' : 'en-US', { month: 'short', timeZone: 'UTC' }).replace(/\.$/, '');
            return isSpanish
              ? `${d.getUTCDate()} ${month} ${hh}:${mm}`
              : `${month} ${d.getUTCDate()} ${hh}:${mm}`;
          })();
          return (
            <Link
              key={g.slug}
              href={`/${isEsports ? 'esports' : 'sports'}/${g.slug}`}
              className="block rounded-[5px] border border-gray-100 dark:border-[#1e1e24] p-2.5 hover:border-gray-300 dark:hover:border-[#2a2a30] hover:bg-gray-50 dark:hover:bg-[#0c0c10]/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                {g.game.live && !g.game.ended ? (
                  <span className="inline-flex items-center gap-1 font-inter-tight text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-[5px] bg-red-500/15 text-red-500 tracking-wide">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                    </span>
                    {t("sportsEvent.live")}
                  </span>
                ) : startTimeText ? (
                  <span className="font-inter-tight text-[10px] text-gray-500 dark:text-gray-400">{startTimeText}</span>
                ) : (
                  <span />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {g.teams.home?.logo ? (
                    <CdnImage src={g.teams.home.logo} alt="" width={22} height={22} className="w-[22px] h-[22px] rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-[22px] h-[22px] rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-400 flex-shrink-0">
                      {g.teams.home?.abbreviation?.charAt(0) || "H"}
                    </div>
                  )}
                  <span className="font-inter-tight text-[13px] font-medium text-gray-900 dark:text-white truncate">{g.teams.home?.name || g.teams.home?.abbreviation}</span>
                </div>
                <span className="font-monument text-xs text-emerald-600 dark:text-[#00FF9D] tabular-nums flex-shrink-0">{formatPrice(hp ?? null)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {g.teams.away?.logo ? (
                    <CdnImage src={g.teams.away.logo} alt="" width={22} height={22} className="w-[22px] h-[22px] rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-[22px] h-[22px] rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-400 flex-shrink-0">
                      {g.teams.away?.abbreviation?.charAt(0) || "A"}
                    </div>
                  )}
                  <span className="font-inter-tight text-[13px] font-medium text-gray-900 dark:text-white truncate">{g.teams.away?.name || g.teams.away?.abbreviation}</span>
                </div>
                <span className="font-monument text-xs text-red-600 dark:text-[#FF1744] tabular-nums flex-shrink-0">{formatPrice(ap ?? null)}</span>
              </div>
              {/* Implicit probability split — visualizes the favorite at a
                  glance. Hidden when prices are missing (e.g. brand-new
                  matches that haven't started trading yet). */}
              {hp != null && ap != null && (
                <div className="h-1 rounded-full bg-gray-100 dark:bg-[#0f0f12] overflow-hidden flex">
                  <div className="h-full bg-emerald-500 dark:bg-[#00FF9D]/70" style={{ width: `${homePct}%` }} />
                  <div className="h-full bg-red-500 dark:bg-[#FF1744]/70" style={{ width: `${awayPct}%` }} />
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
